// @flow

import React from 'react';
import PropTypes from 'prop-types';
import MDEditor from '@uiw/react-md-editor';
import {makeStyles} from '@material-ui/styles';

import textBold from '../../assets/img/text-bold.svg';
import textItalic from '../../assets/img/text-italic.svg';
// import textUnderline from '../../assets/img/text-underline.svg';

import type {ICommand, TextState, TextAreaTextApi} from '@uiw/react-md-editor';

const useStyles = makeStyles(theme => ({
  editorText: {
    height: 'unset !important',
    display: 'flex',
    flexDirection: 'column-reverse',
    borderRadius: '8px',
    '& .w-md-editor-toolbar': {
      height: 'unset !important',
      minHeight: '35px !important',
      marginBottom: theme.spacing(1.5),
      borderBottom: 'none',
      '& ul': {
        display: 'flex',
        marginLeft: 6,
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
      },
      '& ul li button': {
        height: '35px',
      },
      '& ul li button:hover': {
        background: 'none',
      },
      '& ul:first-of-type': {
        // display: 'flex',
        alignItems: 'flex-start',
        flexWrap: 'nowrap',
        gap: '4px',
        height: '100%',
      },
      '& ul:first-of-type li, ul:first-of-type li button': {
        borderRadius: '4px',
        backgroundColor: '#E8E8ED',
        width: '35px',
        height: '35px',
      },
    },
    '& .w-md-editor-text-input ': {
      fontFamily: '"Poppins", "Muli", sans-serif !important',
    },
    '& .w-md-editor-content > div > div > div > pre > code': {
      fontFamily: '"Poppins", "Muli", sans-serif !important',
    },
    '& .w-md-editor-content > div > div > div > pre > code > span .token': {
      color: 'unset !important',
    },
    '& .w-md-editor-content > div > div > div > pre > code > span .italic': {
      fontStyle: 'unset',
    },
  },
}));

type Props = {
  className?: string,
  text: string,
  placeholder?: string,
  extraCommands?: ICommand[],
  leftCommands?: ICommand[],
  onChange: (str: string) => void,
  disabled?: boolean,
};

export type CommandChildProps = {
  close: () => void,
  execute: () => void,
  getState?: () => false | TextState,
  textApi?: TextAreaTextApi,
};

export default function CustomTextEditor(props: Props) {
  const {
    className,
    text,
    onChange,
    placeholder,
    extraCommands,
    disabled,
    leftCommands = [],
  } = props;

  const classes = useStyles();

  const commands: ICommand[] = [
    {
      name: 'Bold',
      enabled: true,
      keyCommand: 'bold',
      buttonProps: {'aria-label': 'Select yout text to bold'},
      icon: <img src={textBold} alt="bold" />,
      execute: (state: TextState, api: TextAreaTextApi) => {
        let modifyText = ` *${state.selectedText}* `;
        if (!state.selectedText) {
          modifyText = ` ** `;
          api.replaceSelection(modifyText);
          api.setSelectionRange({
            start: state.selection.start + 2,
            end: state.selection.start + 2,
          });
        } else {
          api.replaceSelection(modifyText);
          api.setSelectionRange({
            start: state.selection.start + 2,
            end: state.selection.end + 2,
          });
        }
      },
    },
    {
      name: 'Italic',
      enabled: true,
      keyCommand: 'italic',
      buttonProps: {'aria-label': 'Select your text to italic'},
      icon: <img src={textItalic} alt="italic" />,
      execute: (state: TextState, api: TextAreaTextApi) => {
        let modifyText = ` _${state.selectedText}_ `;
        if (!state.selectedText) {
          modifyText = ' __ ';
          api.replaceSelection(modifyText);
          api.setSelectionRange({
            start: state.selection.start + 2,
            end: state.selection.start + 2,
          });
        } else {
          api.replaceSelection(modifyText);
          api.setSelectionRange({
            start: state.selection.start + 2,
            end: state.selection.end + 2,
          });
        }
      },
    },
    ...leftCommands,
  ];

  return (
    <div className={className || undefined} data-color-mode="light">
      <MDEditor
        textareaProps={{
          disabled: disabled,
          placeholder: placeholder ? placeholder : '',
        }}
        preview="edit"
        visibleDragbar={false}
        className={classes.editorText}
        value={text}
        onChange={onChange}
        commands={!disabled ? commands : []}
        extraCommands={extraCommands && !disabled ? extraCommands : []}
      />
    </div>
  );
}

CustomTextEditor.propTypes = {
  className: PropTypes.string,
  text: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  extraCommands: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
